import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { SearchableSelectorComponentModule, SearchBarComponentModule } from '@shared/ui';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

export type WorkspaceSelectorItem = { id: string; name: string };

@Component({
    selector: 'ts-workspace-selector',
    templateUrl: './workspace-selector.component.html',
    styleUrls: ['./workspace-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        TranslateModule,
        SearchBarComponentModule,
        SearchableSelectorComponentModule,
    ]
})
export class WorkspaceSelectorComponent {
  @Input() loaded = false;
  @Input() options: WorkspaceSelectorItem[] = [];

  @Optional() @Input() searchTerm: string | undefined;
  @Optional() @Input() totalSelected = 0;
  @Optional() @Input() selected: WorkspaceSelectorItem[] = [];
  @Optional() @Input() totalAvailable?: number;

  @Output() searchTermChanged = new EventEmitter<string>();
  @Output() lastItemReached = new EventEmitter<void>();
  @Output() added = new EventEmitter<WorkspaceSelectorItem[]>();
  @Output() removed = new EventEmitter<WorkspaceSelectorItem[]>();
  @Output() selectedChange = new EventEmitter<WorkspaceSelectorItem[]>();
  @Output() selectAllAcrossPages = new EventEmitter<void>();

  public readonly ariaLabel: string;
  public readonly noItemsMessage: string;

  constructor(private translateService: TranslateService) {
    this.ariaLabel = this.translateService.instant('title.workspaces');
    this.noItemsMessage = this.translateService.instant('info.no_workspaces');
  }

  public onSelectionChanged(workspaces: WorkspaceSelectorItem[]) {
    this.selected = workspaces;
    this.selectedChange.emit(workspaces);
  }

  public onAdded(workspaces: WorkspaceSelectorItem[]) {
    this.added.emit(workspaces);
  }

  public onRemoved(workspaces: WorkspaceSelectorItem[]) {
    this.removed.emit(workspaces);
  }

  public get allOptions(): WorkspaceSelectorItem[] {
    return this.options || [];
  }

  public onSearchTermChanged(term: string) {
    this.searchTermChanged.emit(term);
  }

  public onLastItemReached() {
    this.lastItemReached.emit();
  }

  public selectionChanged(workspaces: WorkspaceSelectorItem[]) {
    const addedWorkspaces = workspaces.filter((ws) => !this.selected.some((s) => s.id === ws.id));
    const removedWorkspaces = this.selected.filter(
      (ws) => !workspaces.some((s) => s.id === ws.id) && this.options.some((s) => s.id === ws.id)
    );
    const selectedNotInOptions = this.selected.filter((ws) => !this.options.some((s) => s.id === ws.id));
    this.selected = [...workspaces, ...selectedNotInOptions];

    this.selectedChange.emit(this.selected);

    if (addedWorkspaces.length > 0) this.added.emit(addedWorkspaces);
    if (removedWorkspaces.length > 0) this.removed.emit(removedWorkspaces);
  }

  public toggleAllWorkspaces(event: MatCheckboxChange) {
    this.selectionChanged(event.checked ? this.options : []);
  }

  public checkIfWorspaceIsSelected(ws1?: WorkspaceSelectorItem, ws2?: WorkspaceSelectorItem) {
    return ws1?.id === ws2?.id;
  }
}
